// DataTable.tsx — one record set, two presentations.
//
// WHY THIS EXISTS. A nine-column table is the right shape on a desktop and an unusable one on a
// phone: the user sideways-scrolls a viewport-wide strip, loses the column headings off the left
// edge, and cannot compare two records without scrolling twice. Wrapping the table in
// `overflow-x: auto` makes that failure survivable, not good.
//
// So below the breakpoint each row becomes a stacked RECORD CARD — headline, supporting line, then
// its remaining fields as labelled pairs that wrap. Nothing is hidden by default: a field an
// officer can see on a desktop is a field they can see on a phone, because a mobile view that
// quietly drops a column is how someone acts on half a case.
//
// WHY A COMPONENT AND NOT CSS. The CSS-only version of this pattern repeats each column's heading
// inside every cell as `data-label`, so the heading exists in two places and they drift the first
// time one is edited. Here the column is declared ONCE and both presentations are generated from
// it, which is the composition being shared rather than the atoms (CLAUDE.md).
//
// WHAT THIS IS NOT FOR: a cross-tabulation — the ageing transition matrix, for instance — where the
// grid itself carries the meaning and a row has no independent identity. Those stay tables at every
// width and scroll honestly.
import type { ReactNode } from 'react'

export interface Column<T> {
  key: string
  /** Column heading on a desktop; the field label on a phone. Declared once, used for both. */
  header: ReactNode
  cell: (row: T) => ReactNode
  /** Right-aligned and tabular. Figures only — a column of numbers is unreadable ragged-left. */
  num?: boolean
  /**
   * Where this field goes on a phone:
   *   'primary'   the headline of the card — the thing being identified (resident, case, stage)
   *   'secondary' the line under it, muted (an address, a reference)
   *   'trailing'  the right of the headline row — one short token, a tag or a status
   *   undefined   a labelled pair in the wrapping strip below
   * On a desktop this is ignored entirely; every column is just a column.
   */
  role?: 'primary' | 'secondary' | 'trailing'
  /** Suppresses the label on the phone card, for a value that reads for itself. */
  bare?: boolean
  /** Takes the full width of the pair strip. For prose — a next step, a message body. */
  wide?: boolean
}

interface Props<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  /** Shown in place of the table when there are no rows. An empty result is a fact, not a blank. */
  empty: ReactNode
  /** Extra class on the row / card — used for the muted treatment on a blocked case. */
  rowClass?: (row: T) => string | undefined
}

/**
 * Splits the columns into the four slots of the phone card.
 *
 * Exported and pure so it can be tested directly: the failure this guards against is a column
 * being present on the desktop and absent on the phone, which typechecks perfectly and is invisible
 * until an officer acts on a case they could only half read. The test asserts the partition is
 * exhaustive — every column lands in exactly one slot.
 */
export function partitionColumns<T>(columns: Column<T>[]) {
  return {
    primary: columns.filter(c => c.role === 'primary'),
    secondary: columns.filter(c => c.role === 'secondary'),
    trailing: columns.filter(c => c.role === 'trailing'),
    rest: columns.filter(c => !c.role),
  }
}

export default function DataTable<T>({ columns, rows, rowKey, empty, rowClass }: Props<T>) {
  const { primary, secondary, trailing, rest } = partitionColumns(columns)

  if (rows.length === 0) return <p className="muted dt-empty">{empty}</p>

  return (
    <>
      {/* Desktop. Unchanged in every respect from the hand-written tables this replaces. */}
      <table className="tbl dt-wide">
        <thead>
          <tr>{columns.map(c => (
            <th key={c.key} className={c.num ? 'num' : undefined}>{c.header}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={rowKey(r)} className={rowClass?.(r)}>
              {columns.map(c => (
                <td key={c.key} className={c.num ? 'num' : undefined}>{c.cell(r)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Phone. Same columns, same order, stacked. */}
      <ul className="dt-cards">
        {rows.map(r => (
          <li key={rowKey(r)} className={`dt-card ${rowClass?.(r) ?? ''}`}>
            {(primary.length > 0 || trailing.length > 0) && (
              <div className="dt-card-top">
                <div className="dt-card-title">
                  {primary.map(c => <div key={c.key}>{c.cell(r)}</div>)}
                  {secondary.map(c => (
                    <div key={c.key} className="dt-card-sub">{c.cell(r)}</div>
                  ))}
                </div>
                {trailing.length > 0 && (
                  <div className="dt-card-trail">
                    {trailing.map(c => <div key={c.key}>{c.cell(r)}</div>)}
                  </div>
                )}
              </div>
            )}
            {rest.length > 0 && (
              <dl className="dt-pairs">
                {rest.map(c => (
                  <div key={c.key} className={`dt-pair${c.wide ? ' dt-pair-wide' : ''}`}>
                    {!c.bare && <dt>{c.header}</dt>}
                    <dd>{c.cell(r)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}
